<template>
  <div>
    <vue-headful :title="this.setPageTitle('productsInTestImport')" />
    <b-container class="mt-4">
      <!-- Investment Certificates: import a single PDF via DOXA, carried out in the background while the
           steps are shown. The IBIP/Excel flow below is unchanged and shown only for the other asset classes. -->
      <template v-if="isInvestmentCertificates">
        <b-row class="mb-4">
          <b-col class="header">{{ $t('pit.certificate.import') }}</b-col>
        </b-row>

        <b-row class="mb-4">
          <b-col>
            <span class="step-number">1</span>
            <span class="step-title">{{ $t('pit.certificate.stepMetadata') }}</span>
          </b-col>
        </b-row>

        <b-row class="mb-5">
          <b-col cols="12" md="6" lg="4">
            <b-form-group :label="$t('pit.certificate.issuer')" label-class="font-weight-bold">
              <!-- A company owning a single certificates issuer (e.g. BPER) has it preset and locked. -->
              <b-form-select
                v-model="certificateIssuer"
                :options="issuerOptions"
                :disabled="isIssuerFixed || certificateImporting"
              >
                <template #first>
                  <b-form-select-option :value="null" disabled>
                    {{ $t('pit.certificate.selectIssuer') }}
                  </b-form-select-option>
                </template>
              </b-form-select>
            </b-form-group>
            <b-form-group :label="$t('pit.certificate.isin')" label-class="font-weight-bold">
              <b-form-input
                v-model="certificateIdentifier"
                :placeholder="$t('pit.certificate.isinPlaceholder')"
                :disabled="certificateImporting"
              />
            </b-form-group>
            <!-- Reference date is always today: not shown, not selectable; sent as-is to the backend. -->
          </b-col>
        </b-row>

        <b-row class="mb-4">
          <b-col>
            <div class="steps-divider w-50"></div>
          </b-col>
        </b-row>

        <b-row class="mb-4">
          <b-col>
            <span class="step-number">2</span>
            <span class="step-title">{{ $t('pit.certificate.stepFile') }}</span>
          </b-col>
        </b-row>

        <b-row class="mb-5">
          <b-col>
            <b-form-file
              accept=".pdf"
              v-model="certificateFile"
              :disabled="certificateImporting"
              :state="setCertificateInputFileState"
              :placeholder="$t('common.filename')"
              :browse-text="$t('common.browseText')"
              class="form-file-importer mb-1"
            ></b-form-file>
            <span class="invalid-format" v-show="certificateInvalidFormat">
              {{ $t('pit.certificate.fileFormatNotAllowed') }}
            </span>
          </b-col>
        </b-row>

        <b-row class="mb-5">
          <b-col>
            <b-button
              size="sm"
              variant="primary"
              :disabled="!canImportCertificate"
              v-on:click="importCertificate()"
              >{{ $t('pit.certificate.buttonToImport') }}
            </b-button>
          </b-col>
        </b-row>

        <certificate-import-failure-banner
          v-if="certificateFailure"
          :failure="certificateFailure"
          :can-retry="canImportCertificate"
          class="mb-5"
          @retry="importCertificate()"
          @upload-another="uploadAnotherCertificate()"
          @use-market-product="$router.push('/companyProducts')"
        ></certificate-import-failure-banner>

        <div v-if="certificateProgress" id="certificateProgress">
          <b-row class="mb-1">
            <b-col class="header">{{ $t('pit.certificate.progressTitle') }}</b-col>
          </b-row>
          <b-row class="mb-5">
            <b-col>
              <operation-progress :progress="certificateProgress" kind="certificateImport"></operation-progress>
            </b-col>
          </b-row>
        </div>
      </template>

      <template v-else>
      <b-row class="mb-4">
        <b-col class="header">{{ $t('pit.import') }}</b-col>
      </b-row>

      <b-row class="mb-4">
        <b-col>
          <span class="step-number">1</span>
          <div class="step-title d-inline">
            {{ $t('pit.stepOne.term1') }}
            <b-button class="custom-btn font-weight-bold" variant="link" v-on:click="downloadTemplate()">
              Template
            </b-button>
            {{ $t('pit.stepOne.term2') }}
          </div>
        </b-col>
      </b-row>

      <b-row class="mb-5">
        <b-col>
          <div class="mb-2">
            <span>
              <b>{{ $t('pit.guidelinesTitle') }}</b>
            </span>
          </div>
          <ul class="m-0 guidelines">
            <li>{{ $t('pit.guidelines.term1') }}</li>
            <li>{{ $t('pit.guidelines.term2') }}</li>
            <li>{{ $t('pit.guidelines.term3') }}</li>
          </ul>
        </b-col>
      </b-row>

      <b-row class="mb-4">
        <b-col>
          <div class="steps-divider w-50"></div>
        </b-col>
      </b-row>

      <b-row class="mb-4">
        <b-col>
          <span class="step-number">2</span>
          <span class="step-title">{{ $t('pit.stepTwo') }}</span>
        </b-col>
      </b-row>

      <b-row class="mb-5">
        <b-col>
          <b-form-file
            accept=".xlsx"
            v-model="fileToImport"
            :state="setInputFileState"
            :placeholder="$t('common.filename')"
            :browse-text="$t('common.browseText')"
            class="form-file-importer mb-1"
          ></b-form-file>
          <span class="invalid-format" v-show="invalidFormat">
            {{ $t('pit.fileFormatNotAllowed') }}
          </span>
        </b-col>
      </b-row>

      <b-row class="mb-5">
        <b-col>
          <b-button size="sm" variant="primary" :disabled="!fileToImport || invalidFormat" v-on:click="validateFile()"
            >{{ $t('pit.buttonToImport') }}
          </b-button>
        </b-col>
      </b-row>

      <div v-if="showReport" id="report">
        <b-row :class="[lastFileName !== '' ? 'mb-1' : 'mb-3']">
          <b-col class="header">{{ $t('pit.pitReportImport') }}</b-col>
        </b-row>
        <b-row class="mb-2">
          <b-col>
            <p>{{ $t('common.filename') + ': ' + lastFileName }}</p>
          </b-col>
        </b-row>
        <b-row class="mb-5">
          <b-col>
            <pit-card-list
              :title="$t('pit.importedProducts') + ' - ' + importedProducts.length"
              :success-list="importedProducts"
            ></pit-card-list>
          </b-col>
          <b-col>
            <pit-card-list
              :title="$t('pit.importProductsFailed')"
              :arr-error-list="fatalErrors"
              :obj-error-list="productErrors"
              show-errors
            ></pit-card-list>
          </b-col>
        </b-row>
      </div>
      </template>
    </b-container>

    <modal-upload-status
      :id="modalId"
      :data="modalData"
      @on-redirect="$router.push('/companyProducts')"
    ></modal-upload-status>
  </div>
</template>

<script>
import { downloadFile } from '../../utils/fileManager'
import PitCardList from '../../components/Pit/PitCardList'
import ModalUploadStatus from '../../components/Pit/ModalUploadStatus'
import CertificateImportFailureBanner from '../../components/Pit/CertificateImportFailureBanner'
import OperationProgress from '../../components/Lib/OperationProgress'
import { startPolling, OPERATION_STATUS } from '../../utils/operationPoller'

// Slug of the Investment Certificates asset class (matches the backend route slug and the
// available list returned by user/info). The certificate import flow is shown only for this class.
const INVESTMENT_CERTIFICATES_SLUG = 'investment-certificates'

// The steps shown while a certificate is imported: the upload of the file, which only this page knows, and
// the ones the operation reports (reading the PDF, importing the product).
const CERTIFICATE_IMPORT_STEPS = 3

export default {
  components: {
    ModalUploadStatus,
    CertificateImportFailureBanner,
    OperationProgress,
    PitCardList,
  },
  data() {
    const now = new Date()
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
    return {
      file: null,
      fileType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;',
      regex: /.*\.(xlsx|xls)/g,
      loaderColor: 'blue',
      fileFormat: false,
      lastFileName: '',
      modalId: 'modalProductInTestImport',
      modalObject: {
        iconName: '',
        title: '',
        message: '',
      },
      show_report: false,
      isLoading: false,
      fatal_errors: [],
      product_errors: {},
      imported_products: [],

      // Certificate (Investment Certificates) import flow.
      certificate_file: null,
      // Code of the selected issuer: the backend validates it against the company and sends DOXA its name.
      certificateIssuer: null,
      certificateIdentifier: '',
      // Reference date is always today's date (hidden from the user, not selectable).
      certificateDate: today,
      // Where the import of the certificate is, while it goes on; null when none does.
      certificateProgress: null,
      certificateImporting: false,
      // How the last import ended when it failed (the operation as the backend reports it); null otherwise.
      certificateFailure: null,
      issuers: [],
    }
  },

  computed: {
    isInvestmentCertificates() {
      return this.$store.getters.getSelectedAssetClass === INVESTMENT_CERTIFICATES_SLUG
    },

    certificateFile: {
      get() {
        return this.certificate_file
      },
      set(newValue) {
        this.certificate_file = newValue
      },
    },

    certificateInvalidFormat() {
      return this.certificate_file ? /\.pdf$/i.test(this.certificate_file.name) === false : false
    },

    setCertificateInputFileState() {
      return Boolean(this.certificate_file) && !this.certificateInvalidFormat
    },

    // Issuers the user's company may import for: its own certificates issuers, or every certificates
    // issuer when it owns none (e.g. Fairmat).
    issuerOptions() {
      return this.issuers.map((issuer) => ({ value: issuer.code, text: issuer.name }))
    },

    isIssuerFixed() {
      return this.issuers.length === 1
    },

    canImportCertificate() {
      return (
        Boolean(this.certificate_file) &&
        !this.certificateInvalidFormat &&
        Boolean(this.certificateIssuer) &&
        this.certificateIdentifier.trim().length > 0 &&
        Boolean(this.certificateDate) &&
        !this.certificateImporting
      )
    },

    setInputFileState: {
      get() {
        return Boolean(this.fileToImport) && !this.invalidFormat
      },
    },

    fileToImport: {
      get() {
        return this.file
      },
      set(newValue) {
        this.file = newValue
        this.showReport = false
        this.invalidFormat = newValue ? newValue.name.match(this.regex) == null : false
      },
    },

    invalidFormat: {
      get() {
        return this.fileFormat
      },
      set(newValue) {
        this.fileFormat = newValue
      },
    },

    modalData: {
      get() {
        return this.modalObject
      },
      set(newValue) {
        this.modalObject = newValue
      },
    },

    fatalErrors: {
      get() {
        return this.fatal_errors
      },
      set(newValue) {
        this.fatal_errors = newValue
      },
    },

    productErrors: {
      get() {
        return this.product_errors
      },
      set(newValue) {
        this.product_errors = newValue
      },
    },

    importedProducts: {
      get() {
        return this.imported_products
      },
      set(newValue) {
        this.imported_products = newValue
      },
    },

    showReport: {
      get() {
        return this.show_report
      },
      set(newValue) {
        this.show_report = newValue
      },
    },

    errors() {
      return this.$i18n.messages[this.$i18n.locale].pit.errorMessages
    },
  },

  methods: {
    // Loads the issuers selectable by the user's company (asset-class-scoped); a single one is preset.
    loadIssuers() {
      this.get('/api/company/pit/issuers', {})
        .then((resp) => {
          if (resp && Array.isArray(resp.issuers)) {
            this.issuers = resp.issuers.map((el) => ({ code: el.code, name: el.name }))
            if (this.isIssuerFixed) {
              this.certificateIssuer = this.issuers[0].code
            }
          }
        })
        .catch((e) => this.httpErrorsHandler(e))
    },

    // Investment Certificate import: upload a single PDF + metadata to the asset-class-scoped products
    // -in-test endpoint, which answers at once with the operation that carries the import out in the
    // background — the PDF is read through DOXA, then the product is imported. The steps are shown as they
    // go, and the outcome is told when the operation ends. The reference date is always today's.
    importCertificate() {
      if (!this.canImportCertificate) {
        return
      }

      this.certificateImporting = true
      this.certificateFailure = null
      this.certificateProgress = {
        status: 'Running',
        step: 'Uploading',
        stepIndex: 1,
        stepCount: CERTIFICATE_IMPORT_STEPS,
      }
      this.$nextTick(() => {
        const progress = document.getElementById('certificateProgress')
        if (progress) {
          progress.scrollIntoView()
        }
      })

      const formData = new FormData()
      formData.append('File', this.certificate_file, this.certificate_file.name)
      formData.append('IssuerCode', this.certificateIssuer)
      formData.append('Identifier', this.certificateIdentifier.trim())
      formData.append('Date', this.certificateDate)

      this.postFormData('/api/company/pit/upload', formData)
        .then((started) => this.followCertificateImport(started.operationId))
        .catch((e) => {
          this.endCertificateImport()
          this.httpErrorsHandler(e)
          this.certificateFailure = { kind: 'CertificateImport', errorCode: null, errorAction: null, step: 'Uploading' }
        })
    },

    followCertificateImport(operationId) {
      this.certificatePoller = startPolling('certificateImport', () => this.get('/api/operations/' + operationId), {
        // The upload came first: the steps the operation reports follow it.
        onUpdate: (progress) => {
          this.certificateProgress = {
            ...progress,
            stepIndex: progress.stepIndex + 1,
            stepCount: progress.stepCount + 1,
          }
        },
        onFinish: (progress) => this.onCertificateImportFinished(progress),
        onNotFound: () => this.onCertificateImportUnfinished('outcomeUnavailable'),
        onTimeout: () => this.onCertificateImportUnfinished('stillRunning'),
      })
    },

    onCertificateImportFinished(progress) {
      this.endCertificateImport()

      if (progress.status === OPERATION_STATUS.SUCCEEDED) {
        this.successfulModal()
        return
      }

      // The PDF went with the process that was reading it: the file has to be chosen again.
      if (progress.errorCode === 'OperationLost') {
        this.certificate_file = null
      }
      this.certificateFailure = { ...progress, kind: 'CertificateImport' }
    },

    // Command "Upload another PDF": the file and the outcome go, the form is ready again.
    uploadAnotherCertificate() {
      this.certificate_file = null
      this.certificateFailure = null
    },

    // The import goes on, or its outcome is gone: either way this page can say no more than that.
    onCertificateImportUnfinished(messageKey) {
      this.endCertificateImport()
      this.$bvToast.toast(this.$t('pit.certificate.' + messageKey), {
        title: this.$t('pit.certificate.import'),
        variant: 'info',
      })
    },

    endCertificateImport() {
      if (this.certificatePoller) {
        this.certificatePoller.stop()
        this.certificatePoller = null
      }
      this.certificateImporting = false
      this.certificateProgress = null
    },

    downloadTemplate() {
      return downloadFile(
        this,
        'get',
        '/api/company/pit/importTemplate',
        false,
        this.fileType,
        null,
        '',
        this.$t('pit.noFilesAvailable')
      )
    },

    validateFile() {
      if (!this.fileToImport) {
        return alert('Upload a valid excel file before continue.')
      }

      if (this.invalidFormat) {
        return alert(this.$t('pit.fileFormatNotAllowed'))
      }

      // this.isLoading = true;
      this.loadingModal()

      let formData = new FormData()
      formData.append('excel', this.fileToImport, this.fileToImport.name)

      this.postFormData('/api/company/pit/upload', formData)
        .then((resp) => {
          if (resp) {
            this.dispatchErrors(resp)
          }
        })
        .catch(() => {
          this.failedModal()
        })
        .finally(() => {
          this.lastFileName = this.fileToImport.name
          this.fileToImport = null
          this.$nextTick(() => {
            this.showReport = true
            const report = document.getElementById('report')
            if (report) {
              report.scrollIntoView()
            }
          })
        })
    },

    dispatchErrors(errorsList) {
      ;(this.productErrors = {}), (this.fatalErrors = []), (this.importedProducts = [])
      if (!errorsList || Object.keys(errorsList).length === 0) {
        return this.failedModal()
      }

      let genericErrors = errorsList.fatalErrors
      let groupedErrors = errorsList.productErrors
      let imported = errorsList.importedProducts

      /* generic errors */
      if (genericErrors && Array.isArray(genericErrors) && genericErrors.length > 0) {
        this.fatalErrors = genericErrors.map((x) => {
          return {
            row: x.row,
            column: x.column,
            error: this.errors[this.formatFirstStringLetter(x.error)],
          }
        })
      }

      /* products errors needs to be grouped */
      if (groupedErrors && Array.isArray(groupedErrors) && groupedErrors.length > 0) {
        this.productErrors = groupedErrors.reduce((accumulator, reducer) => {
          const code = this.formatFirstStringLetter(reducer.error)
          reducer.error = this.errors[code]
          accumulator[reducer.productName] = accumulator[reducer.productName] || []
          accumulator[reducer.productName].push(reducer)
          return accumulator
        }, {})
      }

      /* successfully imported products */
      if (imported && Array.isArray(imported) && imported.length > 0) {
        this.importedProducts = imported
      }

      return this.fatalErrors.length > 0 || Object.keys(this.productErrors).length > 0
        ? this.failedModal()
        : this.successfulModal()
    },

    formatFirstStringLetter(str) {
      return str.charAt(0).toLowerCase() + str.slice(1)
    },

    // The Excel/IBIP flow; a failed certificate import is told in the banner.
    failedModal() {
      let title = this.$t('pit.importFailed')
      let message = this.$t('pit.failedMessage')

      this.modalData = {
        iconName: 'far fa-times-circle',
        color: 'color: red;',
        title,
        message,
        navigation: false,
        uploading: false,
      }
      this.$bvModal.show('pit-modal')
    },

    loadingModal() {
      this.modalData = {
        iconName: '',
        color: 'color: var(--primary);',
        title: this.$t('pit.pitLoading'),
        message: this.$t('pit.loadingMessage'),
        navigation: false,
        uploading: true,
      }
      this.$bvModal.show('pit-modal')
    },

    successfulModal() {
      this.modalData = {
        iconName: 'far fa-check-circle',
        color: 'color: green;',
        title: this.$t('pit.importSuccessful'),
        message: this.$t('pit.successfullMessage'),
        navigation: true,
        uploading: false,
      }
      this.$bvModal.show('pit-modal')
    },
  },

  beforeDestroy() {
    // The import goes on in the background; this page just stops asking about it.
    this.endCertificateImport()
  },

  mounted() {
    // The issuer dropdown is only used by the certificate flow (Investment Certificates).
    if (this.isInvestmentCertificates) {
      this.loadIssuers()
    }
  },
}
</script>

<style>
.header {
  font-size: 1.5rem;
}
.step-title {
  font-size: 1.3rem;
}
.step-number {
  color: var(--gray7);
  font-size: 3rem;
  margin-right: 1.5rem;
  font-weight: 700;
}
.guidelines {
  max-width: 50%;
}
.guidelines li {
  margin-bottom: 0.3rem;
}
.custom-btn {
  outline: none !important;
  padding: 0 !important;
  text-decoration: none !important;
  font-size: 1.3rem !important;
  margin-bottom: 0.44rem !important;
}
.form-file-importer {
  display: block;
  max-width: 40% !important;
}
.invalid-format {
  font-size: 0.8rem;
  color: red;
  display: block;
}
.steps-divider {
  height: 2px;
  background-color: #dee2e6 !important;
}
</style>
